import { createClient } from '@supabase/supabase-js';

/**
 * 创建具有 service_role 权限的 Supabase 客户端
 * 仅用于服务端操作敏感数据，如 prompt 表
 * 
 * 注意：此客户端绕过 RLS，具有完全数据库访问权限
 * 仅在服务端 API 路由中使用，永远不要在客户端使用
 */
export function createServiceClient() {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const serviceRoleKey = process.env.SUPABASE_SERVICE_ROLE_KEY;

  console.log('[Service Client] Creating service client:', {
    hasUrl: !!supabaseUrl,
    hasServiceKey: !!serviceRoleKey,
    urlLength: supabaseUrl?.length || 0,
    keyLength: serviceRoleKey?.length || 0
  });

  if (!supabaseUrl || !serviceRoleKey) {
    console.error('[Service Client] Missing environment variables:', {
      NEXT_PUBLIC_SUPABASE_URL: !!supabaseUrl,
      SUPABASE_SERVICE_ROLE_KEY: !!serviceRoleKey
    });
    throw new Error('Missing Supabase environment variables for service client');
  }

  const client = createClient(supabaseUrl, serviceRoleKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false
    }
  });

  console.log('[Service Client] Service client created successfully');
  return client;
}

/**
 * 专门用于获取 prompt 数据的函数
 * 只有 service_role 可以访问所有 prompt 数据
 */
export async function getPromptByType(type: string, status: number = 1) {
  console.log(`[Service Client] Fetching prompt:`, { type, status });
  
  const supabase = createServiceClient();
  
  const { data, error } = await supabase
    .from('prompt')
    .select('*')
    .eq('type', type)
    .eq('status', status)
    .single();

  if (error) {
    console.error('[Service Client] Error fetching prompt:', {
      type,
      status,
      error: error.message,
      code: error.code,
      details: error.details,
      hint: error.hint
    });
    throw new Error(`Failed to fetch prompt for type: ${type} - ${error.message}`);
  }

  if (!data) {
    console.error('[Service Client] No prompt data found:', { type, status });
    throw new Error(`No prompt found for type: ${type} with status: ${status}`);
  }

  console.log('[Service Client] Prompt fetched successfully:', {
    id: data.id,
    type: data.type,
    type_name: data.type_name,
    status: data.status,
    promptLength: data.prompt?.length || 0,
    hasPrompt: !!data.prompt
  });

  return data;
}

/**
 * 获取所有激活的 prompt（管理用途）
 */
export async function getAllActivePrompts() {
  const supabase = createServiceClient();
  
  const { data, error } = await supabase
    .from('prompt')
    .select('*')
    .eq('status', 1)
    .order('type');

  if (error) {
    console.error('Error fetching all prompts:', error);
    throw new Error('Failed to fetch prompts');
  }

  return data;
}

/**
 * 创建新的 prompt（管理用途）
 */
export async function createPrompt(promptData: {
  prompt: string;
  type: string;
  type_name?: string;
  status?: number;
}) {
  const supabase = createServiceClient();
  
  const { data, error } = await supabase
    .from('prompt')
    .insert([{
      ...promptData,
      status: promptData.status ?? 1
    }])
    .select()
    .single();

  if (error) {
    console.error('Error creating prompt:', error);
    throw new Error('Failed to create prompt');
  }

  return data;
}

/**
 * 更新 prompt（管理用途）
 */
export async function updatePrompt(id: number, updates: {
  prompt?: string;
  type?: string;
  type_name?: string;
  status?: number;
}) {
  const supabase = createServiceClient();
  
  const { data, error } = await supabase
    .from('prompt')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating prompt:', error);
    throw new Error('Failed to update prompt');
  }

  return data;
}

/**
 * 软删除 prompt（设置 status 为 0）
 */
export async function deletePrompt(id: number) {
  const supabase = createServiceClient();
  
  const { data, error } = await supabase
    .from('prompt')
    .update({ status: 0 })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error deleting prompt:', error);
    throw new Error('Failed to delete prompt');
  }

  return data;
} 